import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaThumbsUp, FaThumbsDown, FaPlus, FaMinus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PostBodyRenderer from './PostBodyRenderer';
import { ConfirmDialog } from '@/components';
import { getMediaUrl } from '@/constants/media';
import { formatName, nameInitial } from '@/utils/formatName';
import SubscriberBadge from '@/components/SubscriberBadge/SubscriberBadge';
import UserActionsMenu from '@/components/UserActionsMenu/UserActionsMenu';
import './Comment.scss';

export interface CommentData {
  id: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  username: string;
  displayName: string;
  /** Matches the SELECT alias `p."avatarUrl" as "avatarUrl"` in all
   *  three CommentController paths. Previously typed as `profilePicture`
   *  — a field the backend never returned, so comment avatars silently
   *  fell back to the initial placeholder for every comment. */
  avatarUrl?: string;
  userId: string;
  likeCount?: number;
  dislikeCount?: number;
  userLiked?: boolean;
  userDisliked?: boolean;
  /** #301: whether this commenter is an active subscriber to the
   *  *post's* creator. Computed server-side on every comment fetch
   *  (LEFT JOIN to app.subscription). Defaults to false when the
   *  field is absent so older response shapes still render cleanly. */
  isSubscriber?: boolean;
  /** ISO timestamp of the subscription's first start (created_at on
   *  app.subscription). Drives the "Subscriber since {Month Year}"
   *  tooltip. May be null even when isSubscriber is true if the row
   *  predates the field — badge still renders, tooltip falls back to
   *  plain "Subscriber". */
  subscriberSince?: string | null;
}

interface CommentProps {
  comment: CommentData;
  currentUserId?: string;
  onEdit?: (commentId: string, newBody: string) => void;
  onDelete?: (commentId: string) => void;
  onLike?: (commentId: string) => void;
  onDislike?: (commentId: string) => void;
  onFollow?: (userId: string) => void;
  isFollowing?: boolean;
}

const Comment: React.FC<CommentProps> = ({
  comment,
  currentUserId,
  onEdit,
  onDelete,
  onLike,
  onDislike,
  onFollow,
  isFollowing = false
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFollowBtn, setShowFollowBtn] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const isOwner = currentUserId === comment.userId;

  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAvatarClick = () => {
    if (isOwner) {
      navigate(`/profile/@${comment.username}`);
    } else if (isMobile) {
      // On mobile, clicking avatar toggles follow button
      setShowFollowBtn(!showFollowBtn);
    }
    // On desktop, clicking avatar does nothing (hover shows follow button, username navigates to profile)
  };

  const handleUsernameClick = () => {
    navigate(`/profile/@${comment.username}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSave = () => {
    if (editBody.trim() && editBody !== comment.body && onEdit) {
      onEdit(comment.id, editBody.trim());
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditBody(comment.body);
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    if (!onDelete) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(comment.id);
      setShowDeleteConfirm(false);
      toast.success('Comment deleted');
    } catch (error) {
      setIsDeleting(false);
      toast.error('Failed to delete comment');
    }
  };

  return (
    <div className={`comment ${isDeleting ? 'deleting' : ''}`}>
      <div
        className="comment-avatar"
        onMouseEnter={() => !isOwner && !isMobile && setShowFollowBtn(true)}
        onMouseLeave={() => !isMobile && setShowFollowBtn(false)}
        onClick={handleAvatarClick}
      >
        {comment.avatarUrl ? (
          <img src={getMediaUrl(comment.avatarUrl) || undefined} alt={comment.username} />
        ) : (
          <div className="avatar-placeholder">
            {nameInitial({ displayName: comment.displayName, username: comment.username })}
          </div>
        )}
        {!isOwner && showFollowBtn && onFollow && (
          <button
            className={`follow-overlay-btn ${isFollowing ? 'following' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onFollow(comment.userId);
              if (isMobile) setShowFollowBtn(false);
            }}
            title={isFollowing ? 'Unfollow' : 'Follow'}
          >
            {isFollowing ? <FaMinus /> : <FaPlus />}
          </button>
        )}
      </div>

      <div className="comment-content">
        <div className="comment-header">
          <div className="comment-author">
            <span className="author-name" onClick={handleUsernameClick}>
              {formatName({ displayName: comment.displayName, username: comment.username })}
            </span>
            {comment.isSubscriber && (
              <SubscriberBadge since={comment.subscriberSince} />
            )}
            <span className="comment-time">{formatDate(comment.createdAt)}</span>
            {comment.createdAt !== comment.updatedAt && (
              <span className="edited-label">(edited)</span>
            )}
          </div>

          {isOwner && !isEditing && (
            <div className="comment-actions">
              <button
                className="action-icon-btn"
                onClick={() => setIsEditing(true)}
                title="Edit"
              >
                ✏️
              </button>
              <button
                className="action-icon-btn delete"
                onClick={handleDeleteClick}
                title="Delete"
                disabled={isDeleting}
              >
                🗑️
              </button>
            </div>
          )}
          {/* Report / block the comment author. Skipped for the author
              themselves (edit/delete buttons above already cover that
              case) and for anon (menu triggers signup-guard on any
              action, so hide the affordance entirely). #371 safety. */}
          {!isOwner && currentUserId && (
            <div className="comment-actions">
              <UserActionsMenu
                targetUserId={comment.userId}
                targetName={formatName({
                  displayName: comment.displayName,
                  username: comment.username,
                })}
              />
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="comment-edit">
            <textarea
              className="edit-textarea"
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              maxLength={500}
              rows={3}
              autoFocus
            />
            <div className="edit-actions">
              <button className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!editBody.trim() || editBody === comment.body}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="comment-body">
              <PostBodyRenderer body={comment.body} />
            </div>

            {/* Reaction buttons */}
            <div className="comment-reactions">
              {onLike && (
                <button
                  className={`reaction-btn like-btn ${comment.userLiked ? 'active' : ''}`}
                  onClick={() => onLike(comment.id)}
                  title="Like"
                >
                  <FaThumbsUp />
                  <span className="reaction-count">{comment.likeCount || 0}</span>
                </button>
              )}
              {onDislike && (
                <button
                  className={`reaction-btn dislike-btn ${comment.userDisliked ? 'active' : ''}`}
                  onClick={() => onDislike(comment.id)}
                  title="Dislike"
                >
                  <FaThumbsDown />
                  <span className="reaction-count">{comment.dislikeCount || 0}</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete comment?"
        message="This comment will be permanently removed. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        isProcessing={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default Comment;
